import { AlertCircle, Plus } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";

export interface TenantCreateDialogProps {
  /** Override the trigger button. */
  trigger?: ReactNode;
  onCreated?: (tenant: { id: string; name: string; slug: string }) => void;
}

export const TenantCreateDialog = ({ trigger, onCreated }: TenantCreateDialogProps) => {
  const client = useUmClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [projectId, setProjectId] = useState<string>("none");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Products the caller may attach the tenant to (superadmin: all).
  const projects = useCall("listProjects");
  const projectOptions = projects.data?.projects ?? [];

  const reset = () => {
    setName("");
    setSlug("");
    setProjectId("none");
    setError(null);
    setLoading(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.createTenant({
      name: name.trim(),
      slug: slug.trim() || undefined,
      projectId: projectId === "none" ? undefined : projectId,
    });
    setLoading(false);
    if (res.ok) {
      onCreated?.({ id: res.data.id, name: res.data.name, slug: res.data.slug });
      reset();
      setOpen(false);
    } else {
      setError(errorMessage(res.error, "Couldn't create tenant."));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button>
            <Plus />
            New tenant
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a new tenant</DialogTitle>
          <DialogDescription>
            Each tenant is an isolated workspace. You'll be added as the owner.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <Field label="Name" htmlFor="tenant-name" required>
            <Input
              id="tenant-name"
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Inc."
            />
          </Field>
          <Field
            label="Slug"
            htmlFor="tenant-slug"
            description="URL-safe identifier. Leave blank to derive from the name."
          >
            <Input
              id="tenant-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
              placeholder="acme"
            />
          </Field>
          {projectOptions.length > 0 ? (
            <Field
              label="Product"
              htmlFor="tenant-project"
              description="Attach the tenant to a product. Gated by the product's tenancy settings."
            >
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger id="tenant-project">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No product</SelectItem>
                  {projectOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" loading={loading} disabled={!name.trim()}>
              Create tenant
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
